import React, { lazy, Suspense, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import ErrorBoundary from "../components/common/ErrorBoundary";
import PrivateRoute from "../components/common/PrivateRoute";
import AdminLayout from "../layouts/AdminLayout";
import ScrollToTop from "./ScrollToTop";

import { getUserRole } from "../services/storage";

const Login = lazy(() => import("../pages/auth/Login"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const AppointmentDetails = lazy(() => import("../pages/appointments/AppointmentDetails"));
const Appointments = lazy(() => import("../pages/appointments/Appointments"));
const BookAppointment = lazy(() => import("../pages/appointments/BookAppointment"));
const BlogApp = lazy(() => import("../pages/blogs/BlogApp"));
const CreateDoctor = lazy(() => import("../pages/doctors/CreateDoctor"));
const Doctors = lazy(() => import("../pages/doctors/Doctors"));
const Enquiries = lazy(() => import("../pages/enquiries/Enquiries"));
const InvestigationPage = lazy(() => import("../pages/lab/InvestigationPage"));
const MailerTester = lazy(() => import("../pages/mail/MailTester"));
const CreatePatient = lazy(() => import("../pages/patients/CreatePatient"));
const CreateSlip = lazy(() => import("../pages/patients/CreateSlip"));
const PatientDetails = lazy(() => import("../pages/patients/PatientDetails"));
const PatientEdit = lazy(() => import("../pages/patients/PatientEdit"));
const Patients = lazy(() => import("../pages/patients/Patients"));
const SlipDetails = lazy(() => import("../pages/patients/SlipDetails"));
const RegistrationList = lazy(() => import("../pages/registrations/RegistrationList"));
const AdminReviewsPanel = lazy(() => import("../pages/reviews/AdminReviewsPanel"));
const ColorSettings = lazy(() => import("../pages/settings/ColorSettings"));
const DoctorPatientNumbering = lazy(() => import("../pages/settings/DoctorPatientNumbering"));
const MakeAddons = lazy(() => import("../pages/addons/MakeAddons"));
const ManageAccounts = lazy(() => import("../pages/accounts/ManageAccounts"));
const AuditLogs = lazy(() => import("../pages/audit/AuditLogs"));
const Reports = lazy(() => import("../pages/reports/Reports"));
const TreatmentEstimateForm = lazy(() => import("../pages/estimates/TreatmentEstimateForm"));
const TreatmentEstimateDetails = lazy(() => import("../pages/estimates/TreatmentEstimateDetails"));
const WebinarRegistrationsAdmin = lazy(() => import("../pages/webinars/WebinarRegistrationsAdmin"));
const InventoryPage = lazy(() => import("../pages/inventory/InventoryPage"));
const CreateSaleInvoicePage = lazy(() => import("../pages/inventory/CreateSaleInvoicePage"));
const SaleInvoiceDetailsPage = lazy(() => import("../pages/inventory/SaleInvoiceDetailsPage"));
const BillingInventorySettingsPage = lazy(() => import("../pages/settings/BillingInventorySettingsPage"));
const PanchakarmaTherapyCalendar = lazy(() => import("../pages/therapies/PanchakarmaTherapyCalendar"));

const AdminOnlyRoute = ({ children }) => {
  return String(getUserRole() || "").toLowerCase() === "admin" ? (
    children
  ) : (
    <Navigate to="/" replace />
  );
};

export default function AppRoutes() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen((value) => !value);

  return (
    <Router>
      <ScrollToTop />
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="grid min-h-[60vh] place-items-center text-sm font-medium text-slate-500">
              Loading…
            </div>
          }
        >
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            element={
              <PrivateRoute>
                <AdminLayout
                  isSidebarOpen={isSidebarOpen}
                  toggleSidebar={toggleSidebar}
                />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="book-appointment" element={<BookAppointment />} />
            <Route
              path="appointment-details/:appointmentId"
              element={<AppointmentDetails />}
            />
            <Route path="create-patient" element={<CreatePatient />} />
            <Route path="patients" element={<Patients />} />
            <Route path="patients/:id" element={<PatientDetails />} />
            <Route path="patients/edit/:id" element={<PatientEdit />} />
            <Route path="patients/create-slip/:id" element={<CreateSlip />} />
            <Route path="slip/:slipId" element={<SlipDetails />} />
            <Route
              path="patients/:patientId/create-estimate"
              element={<TreatmentEstimateForm />}
            />
            <Route
              path="treatment-estimates/:estimateId"
              element={<TreatmentEstimateDetails />}
            />
            <Route
              path="treatment-estimates/:estimateId/edit"
              element={<TreatmentEstimateForm />}
            />
            <Route path="reports" element={<Reports />} />
            <Route path="panchakarma-therapies" element={<PanchakarmaTherapyCalendar />} />
            <Route path="create-doctor" element={<CreateDoctor />} />
            <Route path="doctors" element={<Doctors />} />
            <Route path="enquiries" element={<Enquiries />} />
            <Route path="reviews" element={<AdminReviewsPanel />} />
            <Route path="blogs" element={<BlogApp />} />
            <Route path="mails" element={<MailerTester />} />
            <Route path="register" element={<RegistrationList />} />
            <Route path="colorsettings" element={<ColorSettings />} />
            <Route path="patient-numbering" element={<DoctorPatientNumbering />} />
            <Route path="addons" element={<MakeAddons />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="inventory/create-invoice" element={<CreateSaleInvoicePage />} />
            <Route path="inventory/invoice/:id/edit" element={<CreateSaleInvoicePage />} />
            <Route path="inventory/invoice/:id" element={<SaleInvoiceDetailsPage />} />
            <Route path="billing-settings" element={<BillingInventorySettingsPage />} />
            <Route path="lab" element={<InvestigationPage />} />
            <Route
              path="accounts"
              element={
                <AdminOnlyRoute>
                  <ManageAccounts />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="audit-logs"
              element={
                <AdminOnlyRoute>
                  <AuditLogs />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="admin/webinar-registrations"
              element={<WebinarRegistrationsAdmin />}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}
